import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, Space, message, Form } from 'antd';
import { ArrowRightOutlined, TeamOutlined } from '@ant-design/icons';
import ClientForm, { buildClientFormData } from '../../components/clients/ClientForm';
import { clientsApi, show422Error, ValidationError } from '../../utils/api';
import { FormSkeleton } from '../../components/SkeletonLoaders';
import { useAuth } from '../../contexts/AuthContext';

const AddClient = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('lead');
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [lead, setLead] = useState(null);

  const loadFormOptions = async () => {
    const res = await clientsApi.getCreateForm(leadId);
    const data = res?.data ?? res;
    setCategories(data?.categories || []);
    setSubcategories(data?.subcategories || data?.subCategories || []);
    setSources(data?.sources || []);
    setEmployees(data?.employees || []);
    setLead(data?.lead || null);

    const defaults = {};
    if (user?.id && (data?.employees || []).some((e) => String(e.id) === String(user.id))) {
      defaults.added_by = user.id;
    }
    if (data?.lead) {
      defaults.name = data.lead.client_name || data.lead.name || data.lead.lead_name;
      defaults.email = data.lead.client_email || data.lead.email;
      defaults.mobile = data.lead.mobile;
      if (data.lead.category_id != null) {
        defaults.category_id = data.lead.category_id;
      }
      const leadSourceId = data.lead.source_id ?? data.lead.source?.id;
      if (leadSourceId != null) {
        defaults.source_id = leadSourceId;
      }
    }
    if (Object.keys(defaults).length) {
      form.setFieldsValue(defaults);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await loadFormOptions();
      } catch (e) {
        console.error(e);
        message.error('خطا در دریافت اطلاعات فرم');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleSubmit = async (values, fileOpts) => {
    setSubmitting(true);
    try {
      const fd = buildClientFormData(values, { ...fileOpts, isEdit: false });
      await clientsApi.create(fd);
      message.success('مشتری با موفقیت ثبت شد');
      navigate('/clients');
    } catch (error) {
      if (error instanceof ValidationError || error?.error?.details) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ثبت مشتری');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <TeamOutlined className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  {leadId ? 'تبدیل سرنخ به مشتری' : 'مشتری جدید'}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  {leadId ? 'اطلاعات سرنخ از پیش‌نویس بارگذاری شده است' : 'اطلاعات را تکمیل و ذخیره کنید'}
                </p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/clients')}>
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <FormSkeleton />
            ) : (
              <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 custom-font">
                <ClientForm
                  form={form}
                  loading={false}
                  submitting={submitting}
                  isEdit={false}
                  categories={categories}
                  subcategories={subcategories}
                  sources={sources}
                  employees={employees}
                  lead={lead}
                  onSubmit={handleSubmit}
                  onCancel={() => navigate('/clients')}
                  onRefreshTaxonomy={loadFormOptions}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default AddClient;
